import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { userId, type UserId } from './User.js'

/** Identifier type for public.report_template_ */
export type ReportTemplateId = number & { __brand: 'ReportTemplateId' }

/** Represents the table public.report_template_ */
export default interface ReportTemplateTable {
  /** Database type: pg_catalog.int4 */
  id: ColumnType<
    ReportTemplateId,
    ReportTemplateId | undefined,
    ReportTemplateId
  >

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.text */
  templateName: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  templateType: ColumnType<string, string, string>

  /** Database type: pg_catalog.int4 */
  version: ColumnType<number, number | undefined, number>

  /** Database type: pg_catalog.text */
  description: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.jsonb */
  config: ColumnType<unknown, unknown | undefined, unknown>

  /** Database type: pg_catalog.bool */
  isActive: ColumnType<boolean, boolean | undefined, boolean>
}

export type ReportTemplate = Selectable<ReportTemplateTable>

export type ReportTemplateInitializer = Insertable<ReportTemplateTable>

export type ReportTemplateMutator = Updateable<ReportTemplateTable>

export const reportTemplateId =
  z.number() as unknown as z.Schema<ReportTemplateId>

export const reportTemplate = z.object({
  id: reportTemplateId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  templateName: z.string(),
  templateType: z.string(),
  version: z.number(),
  description: z.string().nullable().optional(),
  config: z.unknown(),
  isActive: z.boolean(),
}) as unknown as z.Schema<ReportTemplate>

export const reportTemplateInitializer = z.object({
  id: reportTemplateId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  templateName: z.string(),
  templateType: z.string(),
  version: z.number().optional(),
  description: z.string().optional().nullable(),
  config: z.unknown().optional(),
  isActive: z.boolean().optional(),
}) as unknown as z.Schema<ReportTemplateInitializer>

export const reportTemplateMutator = z.object({
  id: reportTemplateId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  templateName: z.string().optional(),
  templateType: z.string().optional(),
  version: z.number().optional(),
  description: z.string().optional().nullable(),
  config: z.unknown().optional(),
  isActive: z.boolean().optional(),
}) as unknown as z.Schema<ReportTemplateMutator>
